import { memo, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import type { TableInstance } from 'react-table';
import styled from '@emotion/styled';
import type { Table } from '@tanstack/react-table';

type Props = {
  className?: string;
  canNextPage: boolean;
  canPreviousPage: boolean;
  nextPage: () => unknown;
  pageIndex: number;
  pageSize: number;
  previousPage: () => unknown;
  totalCount: number;
};

export const TablePagination = memo(({
  canNextPage,
  canPreviousPage,
  className,
  nextPage,
  pageIndex,
  pageSize,
  previousPage,
  totalCount,
}: Props) => {

  const display = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = Math.min(start + pageSize, totalCount);
    const startDisplay = Math.min(start + 1, totalCount);
    return `${startDisplay} - ${end} of ${totalCount}`;
  }, [
    pageIndex,
    pageSize,
    totalCount,
  ]);

  return (
    <Root className={className}>
      <Count>{display}</Count>
      <Navigation>
        <StyledChevronLeft
          aria-disabled={!canPreviousPage}
          onClick={canPreviousPage ? previousPage : undefined} />
        <StyledChevronRight
          aria-disabled={!canNextPage}
          onClick={canNextPage ? nextPage : undefined} />
      </Navigation>
    </Root>
  );
});

type RTProps = {
  className?: string;
  tableInstance: TableInstance;
  totalCount?: number;
};

export const ReactTablePagination = ({ className, tableInstance, totalCount }: RTProps) => {
  return (
    <TablePagination
      className={className}
      canPreviousPage={tableInstance.canPreviousPage}
      canNextPage={tableInstance.canNextPage}
      pageIndex={tableInstance.state.pageIndex}
      nextPage={tableInstance.nextPage}
      pageSize={tableInstance.state.pageSize}
      previousPage={tableInstance.previousPage}
      totalCount={totalCount ?? tableInstance.rows.length} />
  );
};

type V8Props = {
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tableInstance: Table<any>;
  totalCount?: number;
};

export const ReactTablev8Pagination = ({ className, tableInstance, totalCount }: V8Props) => {
  return (
    <TablePagination
      className={className}
      canPreviousPage={tableInstance.getCanPreviousPage()}
      canNextPage={tableInstance.getCanNextPage()}
      pageIndex={tableInstance.getState().pagination.pageIndex}
      nextPage={tableInstance.nextPage}
      pageSize={tableInstance.getState().pagination.pageSize}
      previousPage={tableInstance.previousPage}
      totalCount={totalCount ?? tableInstance.getCoreRowModel().rows.length} />
  );
};


type ControlledProps = {
  className?: string;
  goToPage: (pageIndex: number) => unknown;
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  totalCount: number;
};

export const ControlledTablePagination = memo(({
  className,
  goToPage,
  pageCount,
  pageIndex,
  pageSize,
  totalCount,
}: ControlledProps) => {

  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex + 1 < pageCount;
  const previousPage = () => goToPage(pageIndex - 1);
  const nextPage = () => goToPage(pageIndex + 1);

  return (
    <TablePagination
      className={className}
      canPreviousPage={canPreviousPage}
      canNextPage={canNextPage}
      pageIndex={pageIndex}
      nextPage={nextPage}
      pageSize={pageSize}
      previousPage={previousPage}
      totalCount={totalCount} />
  );
});

const Root = styled.div({
  display: 'flex',
  alignItems: 'center',
});

const Count = styled.div(({ theme }) => ({
  marginRight: 15,
  color: theme.palette.gray.dark1,
}));

const Navigation = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  width: 60,
});

const StyledChevronLeft = styled(ChevronLeft)(props => ({
  cursor: props['aria-disabled'] ? 'default' : 'pointer',
  borderRadius: '50%',
  color: props['aria-disabled'] ? props.theme.palette.gray.main : props.theme.palette.black.light2,
  width: 25,
  height: 25,
  transitionProperty: 'color, background-color',
  transitionDuration: '200ms',

  ':hover': {
    backgroundColor: props['aria-disabled'] ? 'unset' : props.theme.palette.gray.light2,
  },
}));

const StyledChevronRight = styled(ChevronRight)(props => ({
  cursor: props['aria-disabled'] ? 'default' : 'pointer',
  borderRadius: '50%',
  color: props['aria-disabled'] ? props.theme.palette.gray.main : props.theme.palette.black.light2,
  width: 25,
  height: 25,
  transitionProperty: 'color, background-color',
  transitionDuration: '200ms',

  ':hover': {
    backgroundColor: props['aria-disabled'] ? 'unset' : props.theme.palette.gray.light2,
  },
}));